import React from 'react';
import AppLayout from '../Layout/AppLayout';

// Category colors map — keys match seed data categories
const CATEGORY_COLORS = {
  'financial': 'hsl(var(--chart-1))',
  'human': 'hsl(var(--chart-2))',
  'structural': 'hsl(var(--chart-3))',
  'customer': 'hsl(var(--chart-4))',
  'wealth': 'hsl(var(--chart-5))',
};

function getCategoryColor(category) {
  if (!category) return '#737373';
  const key = category.toLowerCase().replace(/\s+/g, '_');
  return CATEGORY_COLORS[key] || '#737373';
}

// -- StatusDot (inline) --
function StatusDot({ variant = 'orange' }) {
  const colors = {
    orange: '#f76b15',
    red: 'hsl(var(--destructive))',
    green: 'hsl(var(--chart-2))',
  };
  return (
    <span
      className="inline-block w-2 h-2 rounded-full"
      style={{ backgroundColor: colors[variant] || colors.orange }}
    />
  );
}

// -- ScoreGauge --
function ScoreGauge({ score }) {
  const radius = 80;
  const strokeWidth = 12;
  const circumference = Math.PI * radius;
  const pct = Math.min(Math.max(score, 0), 100);
  const progress = (pct / 100) * circumference;

  return (
    <div className="relative w-[180px] h-[100px] mx-auto">
      <svg width="180" height="100" viewBox="0 0 180 100" className="overflow-visible">
        <defs>
          <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#f76b15" />
            <stop offset="100%" stopColor="rgba(247,107,21,0.7)" />
          </linearGradient>
        </defs>
        {/* Background arc */}
        <path
          d={`M ${strokeWidth / 2} 90 A ${radius} ${radius} 0 0 1 ${180 - strokeWidth / 2} 90`}
          fill="none"
          stroke="hsl(var(--border))"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Progress arc */}
        <path
          d={`M ${strokeWidth / 2} 90 A ${radius} ${radius} 0 0 1 ${180 - strokeWidth / 2} 90`}
          fill="none"
          stroke="url(#scoreGradient)"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`}
          className="transition-all duration-1000"
        />
      </svg>
      <div className="absolute inset-0 flex items-end justify-center pb-2">
        <span className="text-[48px] font-light text-[#0a0a0a]">
          {Math.round(pct)}
        </span>
      </div>
    </div>
  );
}

// -- TierBadge --
const TIER_STYLES = {
  'BEST-IN-CLASS': 'text-[hsl(var(--chart-2))] border-[hsl(var(--chart-2))]',
  'GREEN ZONE': 'text-[hsl(var(--chart-2))]/60 border-[hsl(var(--chart-2))]/60',
  'GREEN_ZONE': 'text-[hsl(var(--chart-2))]/60 border-[hsl(var(--chart-2))]/60',
  'AVERAGE': 'text-[hsl(var(--chart-4))] border-[hsl(var(--chart-4))]',
  'RED FLAG': 'text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]',
  'RED_FLAG': 'text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]',
};

function TierBadge({ tier }) {
  const key = (tier || '').toUpperCase();
  const style = TIER_STYLES[key] || TIER_STYLES['AVERAGE'];
  const label = key.replace(/_/g, ' ');

  return (
    <span className={`inline-block font-mono text-[11px] uppercase rounded-full border px-3 py-1 ${style}`}>
      {label}
    </span>
  );
}

// Determine tier for a category score percentage
function tierForScore(pct) {
  if (pct >= 72) return 'BEST-IN-CLASS';
  if (pct >= 67) return 'GREEN ZONE';
  if (pct >= 58) return 'AVERAGE';
  return 'RED FLAG';
}

// Tier description based on overall tier
function tierDescription(tier) {
  const key = (tier || '').toUpperCase().replace(/_/g, ' ');
  switch (key) {
    case 'BEST-IN-CLASS':
    case 'BEST IN CLASS':
      return 'Your business demonstrates excellent exit readiness across all major categories.';
    case 'GREEN ZONE':
      return 'Your business shows strong fundamentals with only minor areas to address before exit.';
    case 'AVERAGE':
      return 'Your business shows solid fundamentals with room for improvement in key areas.';
    case 'RED FLAG':
      return 'Your business has critical areas that need attention before considering an exit.';
    default:
      return 'Review your category scores below for details.';
  }
}

// Format category name for display
function formatCategory(cat) {
  if (!cat) return 'Unknown';
  return cat
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export default function AssessmentResults({ results }) {

  if (!results) {
    return (
      <AppLayout breadcrumb="ASSESSMENT RESULTS">
        <div className="max-w-[900px] mx-auto px-6 py-12 text-center">
          <p className="text-[15px] text-[#737373]">No assessment results available.</p>
          <a
            href="/succeedo/home"
            className="inline-block mt-6 bg-[#0a0a0a] text-white rounded-full px-6 py-3 font-medium hover:bg-[#262626] transition-colors"
          >
            Return Home
          </a>
        </div>
      </AppLayout>
    );
  }

  const {
    overall_score: rawOverallScore,
    overallScore: rawOverallScoreAlt,
    category_scores: rawCategoryScores,
    categoryScores: rawCategoryScoresAlt,
    red_flags: redFlags = [],
    redFlags: redFlagsAlt,
    green_zones: greenZones = [],
    greenZones: greenZonesAlt,
    tier: rawTier,
    action_plan_item_count: actionPlanItemCount,
    actionPlanItemCount: actionPlanItemCountAlt,
  } = results;

  // Normalize -- the mutation response may come camelized or snake_cased
  const overallScoreRaw = rawOverallScore ?? rawOverallScoreAlt ?? 0;
  // Score is 0-1 from backend; convert to 0-100
  const overallScore = overallScoreRaw <= 1 ? Math.round(overallScoreRaw * 100) : Math.round(overallScoreRaw);
  const categoryScores = rawCategoryScores || rawCategoryScoresAlt || {};
  const flags = redFlags || redFlagsAlt || [];
  const zones = greenZones || greenZonesAlt || [];
  const tier = (rawTier || 'average').toUpperCase().replace(/_/g, ' ');
  const itemCount = actionPlanItemCount ?? actionPlanItemCountAlt ?? 0;

  // Build category score entries
  const categoryEntries = Object.entries(categoryScores).map(([name, score]) => {
    const pct = score <= 1 ? Math.round(score * 100) : Math.round(score);
    return {
      name: formatCategory(name),
      score: pct,
      tier: tierForScore(pct),
      color: getCategoryColor(name),
    };
  });

  return (
    <AppLayout breadcrumb="ASSESSMENT RESULTS">
      <div className="max-w-[900px] mx-auto px-6 py-12">
        {/* Page Label */}
        <div className="flex items-center gap-2 mb-8">
          <StatusDot variant="orange" />
          <span className="font-mono text-[11px] uppercase text-[#737373] tracking-wide">
            ASSESSMENT RESULTS
          </span>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-[48px] font-light text-[#0a0a0a] mb-2">
            Your Exit Readiness Score
          </h1>
          <p className="text-[15px] text-[#737373]">
            Based on your assessment responses
          </p>
        </div>

        {/* Score Gauge */}
        <div className="mb-12">
          <ScoreGauge score={overallScore} />
          <div className="text-center mt-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <StatusDot variant="orange" />
              <span className="font-mono text-[11px] uppercase text-[#737373]">
                {tier}
              </span>
            </div>
            <p className="text-[15px] text-[#737373] max-w-[500px] mx-auto">
              {tierDescription(rawTier)}
            </p>
          </div>
        </div>

        {/* Category Scores */}
        {categoryEntries.length > 0 && (
          <div className="bg-white border border-[#e5e5e5] rounded-[16px] p-6 mb-6">
            <div className="flex items-center gap-2 mb-6">
              <StatusDot variant="orange" />
              <span className="font-mono text-[11px] uppercase text-[#737373] tracking-wide">
                CATEGORY SCORES
              </span>
            </div>
            <div className="space-y-5">
              {categoryEntries.map((category) => (
                <div key={category.name} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[14px] text-[#0a0a0a] font-medium">
                      {category.name}
                    </span>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-[11px] text-[#737373]">
                        {category.score}%
                      </span>
                      <TierBadge tier={category.tier} />
                    </div>
                  </div>
                  <div className="h-1.5 bg-[#e5e5e5] rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all duration-1000"
                      style={{
                        width: `${category.score}%`,
                        backgroundColor: category.color,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* What's Next Card */}
        <div className="bg-card border border-border border-l-2 border-l-[hsl(var(--succeedo-orange))] rounded-[16px] p-6">
          <h2 className="text-[18px] font-semibold text-foreground mb-3">
            Your Personalized 90-Day Plan is Ready
          </h2>
          <p className="text-[15px] text-muted-foreground mb-4">
            Based on your assessment, we&apos;ve created a personalized action plan
            with {itemCount} item{itemCount !== 1 ? 's' : ''} across {categoryEntries.length} categories.
            Each item includes DIY guidance and the option to connect with expert partners.
          </p>

          {/* Red flag summary bullets */}
          {flags.length > 0 && (
            <div className="space-y-2 mb-6">
              {flags.map((flag, index) => {
                const color = getCategoryColor(flag);
                return (
                  <div key={index} className="flex items-start gap-2">
                    <div
                      className="w-1.5 h-1.5 rounded-full mt-2 shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-[14px] text-foreground">
                      <span className="font-medium">{formatCategory(flag)}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <a
            href="/succeedo/action-plan"
            className="inline-block bg-foreground text-background rounded-full py-3 px-8 text-[15px] font-medium hover:bg-foreground/90 transition-colors"
          >
            See Your 90-Day Plan &rarr;
          </a>
        </div>
      </div>
    </AppLayout>
  );
}
